<template>
    <div>
        <v-select
            v-if="selectMode"
            ref="field"
            :id="inputId"
            :items="items"
            :item-text="itemText"
            :item-value="selectedKey"
            :label="labelText"
            :value="value"
            :loading="isLoading"
            :clearable="clearable"
            :disabled="disabled"
            :readonly="readonly"
            :placeholder="placeholder"
            :menu-props="mergedMenuProps"
            :return-object="false"
            :dense="dense"
            :flat="flat"
            :background-color="backgroundColor"
            :hide-details="hideDetails"
            :outlined="outlined"
            :error-messages="errorMessages"
            :prepend-inner-icon="icon ? iconName(icon) : null"
            @input="onSelectInput"
            @click:clear="clearSelection"
            @update:menu="onMenuChange"
            @click="onActivate"
            @focus="onActivate"
        >
            <template v-slot:no-data>
                <v-list-item>
                    <v-list-item-content>
                        <v-list-item-title>{{
                            $translate("no_results")
                        }}</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </template>

            <template v-slot:append-item>
                <v-list-item v-if="hasMore && !isLoading" @click="loadMore">
                    <v-list-item-content>
                        <v-list-item-title
                            ><b>{{ $translate("load_more") }}</b></v-list-item-title
                        >
                    </v-list-item-content>
                </v-list-item>
                <v-progress-linear v-if="isLoading" indeterminate />
            </template>

            <template v-slot:selection="{ item }">
                <slot name="selection" :item="item">
                    {{ itemText(item) }}
                </slot>
            </template>

            <template v-slot:item="{ item }">
                <slot name="item" :item="item">
                    <v-list-item-title>{{ itemText(item) }}</v-list-item-title>
                </slot>
            </template>
        </v-select>
        <v-autocomplete
            v-else
            ref="field"
            :id="inputId"
            :items="items"
            :item-text="itemText"
            :item-value="selectedKey"
            :label="labelText"
            :search-input.sync="localSearch"
            :value="value"
            :loading="isLoading"
            :clearable="clearable"
            :disabled="disabled"
            :readonly="readonly"
            :placeholder="placeholder"
            :menu-props="mergedMenuProps"
            :return-object="false"
            :no-filter="true"
            :dense="dense"
            :flat="flat"
            :background-color="backgroundColor"
            :hide-details="hideDetails"
            :outlined="outlined"
            :error-messages="errorMessages"
            :prepend-inner-icon="icon ? iconName(icon) : null"
            @change="onChange"
            @click:clear="clearSelection"
            @update:menu="onMenuChange"
            @click="onActivate"
            @keyup.native="debouncedEmit(localSearch)"
        >
            <template v-slot:no-data>
                <v-list-item>
                    <v-list-item-content>
                        <v-list-item-title>{{
                            $translate("no_results")
                        }}</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </template>

            <template v-slot:append-item>
                <v-list-item v-if="hasMore && !isLoading" @click="loadMore">
                    <v-list-item-content>
                        <v-list-item-title
                            ><b>{{ $translate("load_more") }}</b></v-list-item-title
                        >
                    </v-list-item-content>
                </v-list-item>
                <v-progress-linear v-if="isLoading" indeterminate />
            </template>

            <template v-slot:item="{ item }">
                <slot name="item" :item="item">
                    <v-list-item-title>{{ itemText(item) }}</v-list-item-title>
                </slot>
            </template>
        </v-autocomplete>
    </div>
</template>

<script>
import debounce from "debounce";

import GlobalMixins from "@/mixins/global";

export default {
    name: "InfiniteSelect",
    mixins: [GlobalMixins],
    props: {
        inputId: String,
        items: { type: Array, default: () => [] },
        value: { type: [String, Number], default: null }, // value = id
        label: { type: String, default: "" },
        itemText: { type: Function, required: true },
        selectedKey: { type: String, default: "id" },
        hasMore: { type: Boolean, default: true },
        loading: { type: Boolean, default: false },
        clearable: { type: Boolean, default: true },
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        placeholder: { type: String, default: "" },
        search: { type: String, default: "" },
        outlined: { type: Boolean, default: false },
        icon: { type: String, default: null },
        flat: { type: Boolean, default: false },
        backgroundColor: { type: String, default: null },
        dense: { type: Boolean, default: true },
        hideDetails: { type: [Boolean, String], default: true },
        errorMessages: {
            type: [String, Array],
            default: null,
        },
        /** Merged into v-autocomplete / v-select menu-props (e.g. contentClass). */
        menuProps: {
            type: Object,
            default: () => ({}),
        },
        /**
         * When true, use v-select (no search field) instead of v-autocomplete.
         * List still loads from parent via load-more / menu-opened.
         */
        selectMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localSearch: this.search,
            menuOpened: false,
            loadedOnce: false,
            isDebouncing: false,
        };
    },
    computed: {
        isLoading() {
            return this.loading || this.isDebouncing;
        },
        labelText() {
            if (this.label === "") {
                return "";
            }
            return this.label || this.$translate("select_an_item");
        },
        mergedMenuProps() {
            return {
                closeOnClick: false,
                closeOnContentClick: false,
                ...this.menuProps,
                contentClass: ["specialized-select-menu", this.menuProps.contentClass].filter(Boolean).join(" "),
            };
        },
    },
    watch: {
        value(newVal) {
            this.syncSearchInputFromValue(newVal, this.items);
        },
        items(newItems) {
            this.syncSearchInputFromValue(this.value, newItems);
        },
        search(newVal) {
            if (this.selectMode) {
                return;
            }
            if (newVal !== this.localSearch) {
                this.localSearch = newVal;
            }
        },
        localSearch(newVal) {
            if (this.selectMode) {
                return;
            }
            if (this.suppressSearchEmit) {
                this.suppressSearchEmit = false;
                return;
            }
            this.debouncedEmit(newVal);
        },
    },

    methods: {
        focus() {
            const el = this.$refs.field;
            if (el && typeof el.focus === "function") {
                el.focus();
            }
        },
        openMenu() {
            this.onActivate();
            const ctrl = this.$refs.field;
            if (!ctrl) {
                return;
            }
            if (typeof ctrl.focus === "function") {
                ctrl.focus();
            }
            this.$nextTick(() => {
                if (typeof ctrl.activateMenu === "function") {
                    ctrl.activateMenu();
                } else if (Object.prototype.hasOwnProperty.call(ctrl, "isMenuActive")) {
                    ctrl.isMenuActive = true;
                }
            });
        },
        syncSearchInputFromValue(val, itemList) {
            if (this.selectMode) {
                return;
            }
            const ctrl = this.$refs.field;
            if (ctrl && ctrl.isMenuActive) {
                return;
            }
            if (this.localSearch) {
                return;
            }
            const item = itemList.find((i) => i[this.selectedKey] === val);
            if (item) {
                this.suppressSearchEmit = true;
                this.localSearch = this.itemText(item);
            }
        },
        onChange(selectedId) {
            this.$emit("input", selectedId);
            const item = this.items.find((i) => i[this.selectedKey] === selectedId);
            this.$emit("select", item || null);

            if (item) {
                this.suppressSearchEmit = true;
                this.localSearch = this.itemText(item);
            }
        },
        onSelectInput(selectedId) {
            this.$emit("input", selectedId);
            const item = this.items.find((i) => i[this.selectedKey] === selectedId);
            this.$emit("select", item || null);
        },
        clearSelection() {
            this.$emit("input", null);
            this.$emit("clear");
            if (!this.selectMode) {
                this.localSearch = "";
                this.$emit("search-changed", "");
            }
        },
        onActivate() {
            if (this.readonly || this.disabled) {
                return;
            }
            if (!this.loadedOnce) {
                this.loadMore();
            }
        },
        onMenuChange(isOpen) {
            this.menuOpened = isOpen;
            if (isOpen) {
                this.onActivate();
                this.$emit("menu-opened");
            }
        },
        loadMore() {
            this.loadedOnce = true;
            this.$emit("load-more");
        },
        setSearch(value) {
            this.isDebouncing = true;
            this.$emit("search-changed", value);
            this.isDebouncing = false;
        },
        debouncedEmit(value) {
            this.isDebouncing = true;
            this.debouncedSearch(value);
        },
    },
    created() {
        this.debouncedSearch = debounce(this.setSearch, 300);
    },
    i18n: {
        messages: {
            en: {
                select_an_item: "Select an item",
                no_results: "No results found",
                load_more: "Load more",
            },
            fr: {
                select_an_item: "Sélectionnez un élément",
                no_results: "Aucun résultat trouvé",
                load_more: "Charger plus",
            },
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/css/_select-menu.scss";
</style>
